import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { ClosingCTA } from '../components/ClosingCTA';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { ConsolidationVisual } from '../components/visuals/ConsolidationVisual';
import { SplitSection, ChecklistSection, CompareTable, ProofStrip, FaqSection, PriceSection, RelatedLinks, serviceJsonLd } from '../components/LocalPage';

/* 2026-09-17: money page 2 of 5. Target query: "best answering service for
   small business" (720 a month, KD 13). This is a comparison query, so the
   page compares the three kinds of service honestly before it makes the
   case for ours. No competitor is named. No reviews, no reputation. */

const faqs = [{
  question: 'What is the best answering service for a small business?',
  answer: 'The one that turns the call into a booked job. For a small business with one or two people on the phone, that means a service that answers around the clock, knows what you do, books into your calendar, texts back the calls it misses, and does not charge more when you get busy. Judge every option on those five things and the choice gets simple.'
}, {
  question: 'Live answering service or AI receptionist, which is better?',
  answer: 'A live service is better if your calls are long, sensitive conversations that need a human. An AI receptionist is better if most calls are the same six questions and the outcome you want is an appointment. For home service businesses it is the second case almost every time, and the AI answers instantly at 2 AM at no extra charge.'
}, {
  question: 'How much should a small business pay for an answering service?',
  answer: 'Live services usually bill per minute, so a plan that looks cheap gets expensive in a busy month. Virtual receptionists tend to run by the call or by the hour. The Viridia System is $897 a month flat and includes the receptionist, a CRM, missed-call text-back, a business number and follow-up automations. Compare total cost in a busy month, not the starting price.'
}, {
  question: 'Will callers know it is an AI?',
  answer: 'It introduces itself as your business and has a normal conversation. If someone asks, it answers honestly. Most callers care about one thing: did they get an appointment. They do.'
}, {
  question: 'Can I keep my existing phone number?',
  answer: 'Yes. We either forward your current line or port it. You also get a dedicated business number registered for texting, which is what makes missed-call text-back deliver instead of getting filtered as spam.'
}, {
  question: 'Is there a contract?',
  answer: 'No. Month to month. If it is not booking you jobs, you stop paying for it.'
}];
const jsonLd = serviceJsonLd({
  name: 'Answering Service for Small Business',
  serviceType: 'AI answering service for small business',
  description: 'AI receptionist for small businesses that answers every call, books appointments, and texts back missed calls. Part of The Viridia System, $897 a month, month to month.',
  path: '/best-answering-service-for-small-business'
});
export function BestAnsweringServiceSmallBusiness() {
  return <>
      <SEOHead title="Best Answering Service for Small Business: How to Pick One | Viridia" description="The best answering service for small business is the one that books the job. Compare live answering, virtual receptionists and AI receptionists on the five things that matter, with flat pricing at $897 a month." canonical="/best-answering-service-for-small-business" jsonLd={jsonLd} faq={faqs} data-id="element-1392" data-mp="3vvnds2550t9y" />

      <PageHeader label="Reception" title="The best answering service for small business is the one that books the job." intro="Every answering service picks up the phone. Very few of them put an appointment on your calendar. Here is how to tell the difference before you sign up for one." data-id="element-1393" data-mp="1684xf9e2t1nb">
        <div className="flex flex-wrap items-center gap-4" data-id="element-1394" data-mp="32c2l2w2iqp18">
          <CTAButton to="/contact" data-id="element-1395" data-mp="2y9g09f49m4dj">Book a 15-min call</CTAButton>
          <CTAButton to="/answering-service-for-contractors" variant="ghost" data-id="element-1396" data-mp="2yc24p5vv03rj">
            For contractors
          </CTAButton>
        </div>
      </PageHeader>

      <ChecklistSection label="How to judge one" title="Five questions to ask any answering service." body="If the answer to any of these is no, you are paying for a message-taking service, and voicemail already does that for free." items={[{
      title: 'Does it answer at 9 PM on a Saturday?',
      body: 'Small businesses lose the most calls outside office hours. A service that adds an after-hours fee is telling you when it expects to miss calls.'
    }, {
      title: 'Does it know what you do?',
      body: 'A receptionist reading a generic script cannot tell a caller whether you handle their job. Yours should know your services, your area and your hours.'
    }, {
      title: 'Does it book the appointment?',
      body: 'A message you have to return is a lead you have to chase. The service should put the appointment on your calendar with the details.'
    }, {
      title: 'What happens to the calls it misses?',
      body: 'Every service misses some. The good ones text the caller back in seconds so the lead is saved even when nobody picked up.'
    }, {
      title: 'What does a busy month cost?',
      body: 'Per-minute and per-call billing punishes you for growing. Ask for the price in your busiest month, not the plan minimum.'
    }, {
      title: 'Who follows up tomorrow?',
      body: 'A caller who wanted to think about it should hear from you again. Ask whether the service does that, or whether that job is still on you.'
    }]} data-id="element-1397" data-mp="2octdulwaj59e" />

      <section className="border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-1398" data-mp="3etdbl4ow4cz4">
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-1399" data-mp="lzolpop7ahyi">
          <h2 className="max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-1400" data-mp="lxu16bhucruy">
            The three kinds of answering service, side by side.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-1401" data-mp="voyez7vs66o7">
            Each one is a real option and each has a place. The table shows where they differ on
            the five questions above.
          </p>
          <div className="mt-12" data-id="element-1402" data-mp="32nc1qkpbto8">
            <CompareTable columns={['Live answering service', 'Virtual receptionist', 'Viridia AI receptionist']} highlight={2} rows={[{
            criterion: 'Answers around the clock',
            cells: ['Often an add-on', 'Business hours', true]
          }, {
            criterion: 'Knows your services and area',
            cells: ['Script', 'Learns over time', 'Built with you at setup']
          }, {
            criterion: 'Books into your calendar',
            cells: ['Takes a message', 'Sometimes', true]
          }, {
            criterion: 'Texts back missed calls',
            cells: [false, false, true]
          }, {
            criterion: 'Follows up on its own',
            cells: [false, false, true]
          }, {
            criterion: 'Best for',
            cells: ['Long, sensitive calls', 'Admin and scheduling', 'Booking jobs from inbound calls']
          }, {
            criterion: 'Billing',
            cells: ['Per minute', 'Per call or hourly', 'Flat, $897 a month']
          }]} data-id="element-1403" data-mp="3u7wxr61nxls1" />
          </div>
        </div>
      </section>

      <SplitSection label="What ours does" title="It has the conversation you would have, then books it." body={<>
            <p data-id="element-1405" data-mp="24ycoycumcseg">
              The AI receptionist is built with you on a setup call. It knows your services, your
              pricing rules, your service area and your calendar.
            </p>
            <p data-id="element-1406" data-mp="2xeneudeuk0kj">
              A caller gets a normal conversation, an appointment, and a confirmation text. You get
              the details on your phone and a calendar entry you did not have to make.
            </p>
          </>} visual={<DeviceMockup mode="call" heading="Incoming call, Saturday 7:05 PM" lines={[{
      from: 'caller',
      text: 'Hi, do you guys do interior painting? I have three bedrooms.'
    }, {
      from: 'ai',
      text: 'We do. I can get an estimator out Tuesday at 10 or Wednesday at 2. Which works?'
    }, {
      from: 'caller',
      text: 'Tuesday at 10.'
    }]} resultChip="Estimate booked, confirmation sent" data-id="element-1407" data-mp="sk4jln1qrm5n" />} note="AI receptionist" data-id="element-1404" data-mp="2en4gijvrpaou" />

      <SplitSection alt flip label="Why it is different" title="The receptionist is one piece of a system, not a standalone bill." body={<>
            <p data-id="element-1409" data-mp="3rygde0o5txp1">
              An answering service on its own still leaves you with a CRM to buy, a scheduling app,
              and a follow-up process that lives in your head.
            </p>
            <p data-id="element-1410" data-mp="3cvycigtp2774">
              The Viridia System puts the receptionist, the CRM, missed-call text-back, your business
              number and the follow-up automations in one place, for one flat price.
            </p>
          </>} visual={<ConsolidationVisual data-id="element-1411" data-mp="3rnqtex94ztay" />} note="The Viridia System" data-id="element-1408" data-mp="12bhbgenjicbo" />

      <ProofStrip items={[{
      value: '284%',
      label: 'more leads for a Phoenix landscaping company after the system went in',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '28',
      label: 'estimates booked by the AI receptionist in that same account',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '29',
      label: 'estimates booked by the receptionist for a Valley painting company',
      to: '/case-studies/finish-coat-painting',
      source: 'Finish Coat Painting'
    }]} data-id="element-1412" data-mp="3voj1hn69vs5n" />

      <PriceSection title="One flat price, whatever the phone does this month." body={<p data-id="element-1414" data-mp="2asjee09kvbcr">
            No per-minute billing, no after-hours surcharge, no contract. The receptionist, the CRM,
            missed-call text-back, a business number and the automations, built with you.
          </p>} data-id="element-1413" data-mp="2g9mq30gnsst8" />

      <FaqSection alt title="Questions small business owners ask." faqs={faqs} data-id="element-1415" data-mp="2oqhimcu6prej" />

      <RelatedLinks title="Related pages" links={[{
      to: '/answering-service-for-contractors',
      label: 'Answering service for contractors',
      body: 'The trades version: emergencies, service areas, and estimates booked from the truck.'
    }, {
      to: '/answering-service-phoenix',
      label: 'Answering service in Phoenix',
      body: 'Service area, hours, and the Valley businesses we already answer for.'
    }, {
      to: '/blog/ai-receptionist-vs-human-receptionist-cost',
      label: 'AI vs human receptionist cost',
      body: 'The real math on what each one costs a small business per month.'
    }]} data-id="element-1416" data-mp="2tpau5zpwv8c2" />

      <ClosingCTA data-id="element-1417" data-mp="3rjljr1sexxvw" />
    </>;
}